import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Globe, MapPin, Users, CheckCircle2, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  COUNTRY_CENTROIDS, US_STATE_CENTROIDS,
  projectEquirect, projectUS,
} from "@/lib/geoCentroids";

/**
 * Admin geo dashboard for Ronin Box leads.
 * Shows: stats tiles + SVG world map (country bubbles) + SVG US map (state bubbles)
 * + side-by-side leaderboards (top countries / states / postal codes).
 *
 * Data source: GET /api/admin/ronin-leads/geo-stats
 */
export function RoninGeoDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/ronin-leads/geo-stats");
      setStats(data);
    } catch {
      toast.error("Failed to load geo stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/ronin-leads/backfill-geo");
      toast.success(`Backfilled ${data.updated || 0} leads with city/region`);
      await load();
    } catch {
      toast.error("Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading && !stats) {
    return <div className="text-sm text-zinc-500">Loading geo dashboard…</div>;
  }
  if (!stats || stats.total === 0) {
    return (
      <div className="border border-dashed border-[#27272A] p-10 text-center text-zinc-500 rounded-sm" data-testid="geo-empty">
        <Globe className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
        Geo dashboard activates after the first signup.
      </div>
    );
  }

  const topCountries = stats.top_countries || [];
  const topStates = stats.top_us_states || [];
  const topPostals = stats.top_postals || [];
  const maxCountry = Math.max(1, ...topCountries.map((c) => c.count));
  const maxState = Math.max(1, ...topStates.map((s) => s.count));

  return (
    <div className="space-y-8" data-testid="geo-dashboard">
      {/* Stats tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#27272A] border border-[#27272A] rounded-sm overflow-hidden">
        <StatTile icon={Users}        label="Total leads"  value={stats.total} />
        <StatTile icon={CheckCircle2} label="Verified"     value={stats.verified} color="text-emerald-400" />
        <StatTile icon={Clock}        label="Pending"      value={stats.pending} color="text-amber-400" />
        <StatTile icon={Globe}        label="Countries"    value={stats.country_count} />
        <StatTile icon={MapPin}       label="US states"    value={stats.us_state_count || 0} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-xl text-white uppercase tracking-tight">Where signups come from</h3>
        <Button
          variant="outline" size="sm"
          onClick={backfill} disabled={backfilling}
          className="border-[#27272A] bg-[#0A0A0A] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-[10px]"
          data-testid="geo-backfill-btn"
          title="Re-resolves city/region for older signups missing geo metadata"
        >
          <RefreshCw className={`h-3 w-3 mr-2 ${backfilling ? "animate-spin" : ""}`} />
          Backfill geo
        </Button>
      </div>

      {/* World + US maps */}
      <div className="grid lg:grid-cols-2 gap-6">
        <WorldMap countries={topCountries} max={maxCountry} />
        <USMap states={topStates} max={maxState} />
      </div>

      {/* Leaderboards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Leaderboard
          title="Top countries"
          rows={topCountries.slice(0, 8).map((c) => ({ key: c.country, label: c.country, count: c.count }))}
          testid="leaderboard-countries"
        />
        <Leaderboard
          title="Top US states"
          rows={topStates.slice(0, 8).map((s) => ({ key: s.code, label: `${s.code} — ${s.name}`, count: s.count }))}
          testid="leaderboard-states"
          emptyHint="No US signups yet."
        />
        <Leaderboard
          title="Top postal codes"
          rows={topPostals.slice(0, 8).map((p) => ({
            key: `${p.country}-${p.postal}`,
            label: `${p.postal}${p.place ? ` · ${p.place}` : ""}${p.region ? `, ${p.region}` : ""}`,
            sub: p.country,
            count: p.count,
          }))}
          testid="leaderboard-postals"
        />
      </div>
    </div>
  );
}

// ─── World map ──────────────────────────────────────────────────────────
function WorldMap({ countries, max }) {
  const W = 1000, H = 500;
  // Grid lines: every 30° lon, 30° lat
  const gridLons = [-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150];
  const gridLats = [-60, -30, 0, 30, 60];

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] rounded-sm p-4" data-testid="geo-world-map">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">World view</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <rect x={0} y={0} width={W} height={H} fill="#050505" />
        {/* Grid */}
        {gridLons.map((lon) => {
          const [x] = projectEquirect(lon, 0, W, H);
          return <line key={`vl-${lon}`} x1={x} y1={0} x2={x} y2={H} stroke="#1a1a1a" strokeWidth={1} />;
        })}
        {gridLats.map((lat) => {
          const [, y] = projectEquirect(0, lat, W, H);
          return <line key={`hl-${lat}`} x1={0} y1={y} x2={W} y2={y} stroke="#1a1a1a" strokeWidth={1} />;
        })}
        {/* Equator + prime meridian — slightly brighter */}
        <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="#27272A" strokeWidth={1} />
        <line x1={W / 2} y1={0} x2={W / 2} y2={H} stroke="#27272A" strokeWidth={1} />

        {/* Bubbles */}
        {countries.map((c) => {
          const coords = COUNTRY_CENTROIDS[c.country];
          if (!coords) return null;
          const [lon, lat] = coords;
          const [cx, cy] = projectEquirect(lon, lat, W, H);
          const radius = 6 + (c.count / max) * 22; // 6–28
          return (
            <g key={c.country}>
              <circle cx={cx} cy={cy} r={radius} fill="#FF331F" fillOpacity={0.25} stroke="#FF331F" strokeWidth={1.5} />
              <circle cx={cx} cy={cy} r={4} fill="#FF331F" />
              <text x={cx + radius + 4} y={cy + 4} fontSize={13} fontFamily="Outfit, sans-serif" fill="#fff">
                {c.country} · {c.count}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-[10px] text-zinc-600 mt-2">Bubble size = lead volume · Equirectangular projection.</p>
    </div>
  );
}

// ─── US map ────────────────────────────────────────────────────────────
function USMap({ states, max }) {
  const W = 1000, H = 600;
  // Lat grid: 25, 30, 35, 40, 45, 50 (N)
  const gridLats = [25, 30, 35, 40, 45, 50];
  const gridLons = [-120, -110, -100, -90, -80, -70];

  // Pin AK / HI / PR in custom positions (insets) instead of true geo coords
  const insetPositions = { AK: [80, H - 90], HI: [180, H - 90], PR: [W - 100, H - 90] };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] rounded-sm p-4" data-testid="geo-us-map">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">United States</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <rect x={0} y={0} width={W} height={H} fill="#050505" />
        {/* Grid lines */}
        {gridLons.map((lon) => {
          const [x] = projectUS(lon, 40, W, H);
          return <line key={`uvl-${lon}`} x1={x} y1={0} x2={x} y2={H - 130} stroke="#1a1a1a" strokeWidth={1} />;
        })}
        {gridLats.map((lat) => {
          const [, y] = projectUS(-95, lat, W, H);
          return <line key={`uhl-${lat}`} x1={0} y1={y} x2={W} y2={y} stroke="#1a1a1a" strokeWidth={1} />;
        })}
        {/* Inset divider */}
        <line x1={0} y1={H - 130} x2={W} y2={H - 130} stroke="#27272A" strokeWidth={1} strokeDasharray="4 4" />
        <text x={20} y={H - 110} fontSize={10} fontFamily="Outfit, sans-serif" fill="#52525B" letterSpacing="2">
          AK · HI · PR (insets)
        </text>

        {states.map((s) => {
          const coords = US_STATE_CENTROIDS[s.code];
          if (!coords) return null;
          let cx, cy;
          if (insetPositions[s.code]) {
            [cx, cy] = insetPositions[s.code];
          } else {
            const [lon, lat] = coords;
            [cx, cy] = projectUS(lon, lat, W, H);
          }
          const radius = 8 + (s.count / max) * 28; // 8–36
          return (
            <g key={s.code}>
              <circle cx={cx} cy={cy} r={radius} fill="#FF331F" fillOpacity={0.25} stroke="#FF331F" strokeWidth={1.5} />
              <text x={cx} y={cy + 4} fontSize={11} fontFamily="Oswald, sans-serif" fontWeight={700} fill="#fff" textAnchor="middle">
                {s.code}
              </text>
              <text x={cx} y={cy + radius + 14} fontSize={11} fontFamily="Outfit, sans-serif" fill="#a1a1aa" textAnchor="middle">
                {s.count}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-[10px] text-zinc-600 mt-2">State bubbles · Equirectangular projection over lower 48.</p>
    </div>
  );
}

// ─── Stat tile / leaderboard ──────────────────────────────────────────────
function StatTile({ icon: Icon, label, value, color = "text-white" }) {
  return (
    <div className="bg-[#0A0A0A] p-4">
      <Icon className={`h-4 w-4 ${color} mb-2`} />
      <div className={`font-display text-3xl ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">{label}</div>
    </div>
  );
}

function Leaderboard({ title, rows, testid, emptyHint }) {
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] rounded-sm p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">{title}</p>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-600 py-4">{emptyHint || "No data yet."}</p>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.key} className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-zinc-600 font-mono text-[11px] w-5">{i + 1}</span>
                <span className="text-white truncate">{r.label}</span>
              </div>
              <span className="font-display text-lg text-[#FF331F]">{r.count}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
